<template>
  <div class="p-3">
    <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>

    <div class="loading" v-if="fetchData">
      <load-data></load-data>
    </div>

    <div v-else>
      <div v-if="!displayGroups.length" class="text-muted small py-2">
        No courses in this group.
      </div>

      <div v-else class="col-12 mx-auto bg-white p-2 mt-2">
        <div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0 font-weight-bold">
              <i class="fas fa-book text-success mr-2"></i> Enroll Exam Students for Group: {{ group.name || '—' }}
            </h5>
            <b-button size="sm" variant="outline-success" class="mr-2" @click="openEligibilityWizard">
              Issue Hall Tickets
            </b-button>
            <!-- <b-button size="sm" variant="danger" @click="$emit('close')">Close</b-button> -->
          </div>
        </div>
        <div class="">
        <table class="table table-bordered table-striped coe-group-table">
          <thead class="thead-dark">
            <tr ref="groupHeaderRow">
              <th style="width:50px" rowspan="2">Sl.No</th>
              <th style="width:130px" rowspan="2">Course Code</th>
              <th rowspan="2">Course Name</th>
              <th rowspan="2">Course Offering</th>
              <th style="width:130px" rowspan="2">Enrolled Students</th>
              <th style="width:130px" rowspan="2">Exam Students</th>
              <th style="width:130px" rowspan="2">Total Enrolled</th>
              <th style="width:130px" rowspan="2">Total Selected</th>
              <th colspan="4" class="text-center">Question Paper</th>
              <th colspan="3" class="text-center">Evaluation</th>
              <th style="width:120px" rowspan="2">Assign to Set</th>
              <th style="width:110px" rowspan="2">Class Code</th>
              <th style="width:110px" rowspan="2">Academic Year</th>
              <th rowspan="2">Program</th>
            </tr>
            <tr :style="{ top: stickyHeaderOffset + 'px' }">
              <!-- widths must match the qp-set-inner-table colgroup below -->
              <th class="qp-set-col-set">Set</th>
              <th class="qp-set-col-faculty">Faculty</th>
              <th class="qp-set-col-status">Status</th>
              <th class="qp-set-col-edit-until">Edit Until</th>
              <th style="width:110px">Evaluation Type</th>
              <th style="width:200px">Evaluator(s)</th>
              <th style="width:130px">Evaluator Mapping</th>
            </tr>
          </thead>
          <tbody
            v-for="(grp, index) in displayGroups"
            :key="grp.code"
            :class="grp.shaded ? 'group-shaded' : ''"
          >
            <tr>
              <td :rowspan="grp.courses.length" class="align-middle text-center">{{ index + 1 }}</td>
              <td :rowspan="grp.courses.length" class="align-middle font-weight-bold">
                {{ grp.code }}
                <i
                  v-if="grp.nameMismatch"
                  class="fas fa-exclamation-triangle text-warning ml-1"
                  v-b-tooltip.hover
                  :title="'Source names: ' + grp.sourceNames.join(', ')"
                  style="cursor:pointer"
                ></i>
              </td>
              <td :rowspan="grp.courses.length" class="align-middle font-weight-bold">
                {{ grp.courses[0].name }}
                <i
                  v-if="grp.nameMismatch"
                  class="fas fa-exclamation-triangle text-warning ml-1"
                  v-b-tooltip.hover
                  :title="'Source names: ' + grp.sourceNames.join(', ')"
                  style="cursor:pointer"
                ></i>
                <div class="small font-weight-normal">
                  <a href="#" class="text-info" @click.prevent="openExamDatesModal">Date of Exam</a>
                  <span class="text-muted ml-1">{{ examDateSummary(grp.code) }}</span>
                </div>
              </td>
              <td class="align-middle">{{ grp.courses[0].name }}</td>
              <td class="text-center">
                <span
                  class="text-info btn p-0 m-0"
                  @click="viewEnrolledStudents(grp.courses[0])"
                >{{ loadingCounts ? '...' : grp.courses[0].enrolled.length }}</span>
              </td>
              <td class="text-center">
                <span
                  class="text-info btn p-0 m-0"
                  @click="openAddStudentsModal(grp.courses[0])"
                >{{ loadingCounts ? '...' : grp.courses[0].selectedIds.length }}</span>
              </td>
              <td :rowspan="grp.courses.length" class="align-middle text-center">
                <span
                  class="text-info btn p-0 m-0"
                  @click="viewTotalEnrolledStudents(grp)"
                >{{ loadingCounts ? '...' : grp.totalEnrolledStudents.length }}</span>
              </td>
              <td :rowspan="grp.courses.length" class="align-middle text-center">
                <span
                  class="text-info btn p-0 m-0"
                  @click="openGroupSelectionModal(grp)"
                >{{ loadingCounts ? '...' : grp.totalSelectedStudents.length }}</span>
              </td>
              <td :rowspan="grp.courses.length" colspan="4" class="align-middle p-0">
                <table class="table table-bordered table-sm qp-set-inner-table mb-0">
                  <colgroup>
                    <col class="qp-set-col-set">
                    <col class="qp-set-col-faculty">
                    <col class="qp-set-col-status">
                    <col class="qp-set-col-edit-until">
                  </colgroup>
                  <tbody>
                    <tr v-for="set in grp.qpSets" :key="set.label">
                      <td class="font-weight-bold align-middle">
                        Set {{ set.label }} <span class="text-muted font-weight-normal">({{ set.student_count }})</span>
                      </td>
                      <td class="align-middle">
                        <span v-if="loadingQpSets" class="text-muted">...</span>
                        <a
                          v-else
                          href="#"
                          class="text-info"
                          @click.prevent="openQpFacultyModal(grp, set)"
                        >{{ set.faculty_names.length ? set.faculty_names.join(', ') : 'No Faculties' }}</a>
                      </td>
                      <td class="align-middle">
                        <template v-if="!loadingQpSets">
                          <b-badge v-if="set.submitted_at" variant="success" class="mr-1">Submitted</b-badge>
                          <b-badge v-else variant="secondary" class="mr-1">Not Submitted</b-badge>
                          <a v-if="set.submitted_at" href="#" class="text-primary" @click.prevent="viewQuestionPaper(set)">View</a>
                          <a v-if="set.log_count" href="#" class="text-secondary ml-1" @click.prevent="openQuestionLog(grp, set)">Log ({{ set.log_count }})</a>
                        </template>
                      </td>
                      <td class="align-middle">
                        <template v-if="!loadingQpSets">
                          <b-form-input
                            type="date"
                            size="sm"
                            :disabled="resultPublished"
                            :value="set.edit_until"
                            @change="saveQpEditWindow(grp, set, { edit_until: $event || null })"
                          />
                          <b-form-checkbox
                            switch
                            size="sm"
                            class="mt-1"
                            :disabled="resultPublished"
                            :checked="set.allow_edit_after_deadline"
                            @change="saveQpEditWindow(grp, set, { allow_edit_after_deadline: $event })"
                          >Allow after date</b-form-checkbox>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td :rowspan="grp.courses.length" class="align-middle">
                <span v-if="loadingEvaluations" class="text-muted">...</span>
                <b-badge v-else variant="light" class="border">
                  {{ grp.evaluation.evaluation_type === 'double' ? 'Double' : 'Single' }}
                </b-badge>
              </td>
              <td :rowspan="grp.courses.length" class="align-middle">
                <span v-if="loadingEvaluations" class="text-muted">...</span>
                <template v-else>
                  <div>
                    <a href="#" class="text-info" @click.prevent="openEvaluationModal(grp)">
                      Evaluator 1: {{ grp.evaluation.evaluator_1.length ? grp.evaluation.evaluator_1.map(e => e.name).join(', ') : 'Not assigned' }}
                    </a>
                  </div>
                  <div v-if="grp.evaluation.evaluation_type === 'double'">
                    <a href="#" class="text-info" @click.prevent="openEvaluationModal(grp)">
                      Evaluator 2: {{ grp.evaluation.evaluator_2.length ? grp.evaluation.evaluator_2.map(e => e.name).join(', ') : 'Not assigned' }}
                    </a>
                  </div>
                </template>
              </td>
              <td :rowspan="grp.courses.length" class="align-middle text-center">
                <a href="#" class="text-info" @click.prevent="openEvaluatorStudentsModal(grp)">Assign Students</a>
              </td>
              <td :rowspan="grp.courses.length" class="align-middle text-center">
                <a href="#" class="text-info" @click.prevent="openQpSetStudentsModal(grp)">Assign to Sets</a>
              </td>
              <td>{{ grp.courses[0].class_code }}</td>
              <td>{{ grp.courses[0].academic_year+'-'+grp.courses[0].academic_semester}}</td>
              <td>{{ grp.courses[0].program_name }}</td>
            </tr>
            <tr v-for="course in grp.courses.slice(1)" :key="course.id">
              <td class="align-middle">{{ course.name }}</td>
              <td class="text-center">
                <span
                  class="text-info btn p-0 m-0"
                  @click="viewEnrolledStudents(course)"
                >{{ loadingCounts ? '...' : course.enrolled.length }}</span>
              </td>
              <td class="text-center">
                <span
                  class="text-info btn p-0 m-0"
                  @click="openAddStudentsModal(course)"
                >{{ loadingCounts ? '...' : course.selectedIds.length }}</span>
              </td>
              <td>{{ course.class_code }}</td>
              <td>{{ course.academic_year+'-'+course.academic_semester }}</td>
              <td>{{ course.program_name }}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    </div>

    <!-- Editable merged selection across all offerings (Total Exam Students) -->
    <b-modal id="coeQuestionLogModal" :title="questionLogTitle" hide-footer centered size="xl" @hidden="questionLogExamId = null">
      <ExamQuestionLogs v-if="questionLogExamId" :examId="questionLogExamId" />
    </b-modal>

    <b-modal id="coeGroupStudentsModal" :title="modalTitle" hide-footer centered size="lg">
      <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>
      <div v-if="!modalGroupStudents.length" class="text-muted small py-2">No students enrolled in this group.</div>
      <template v-else>
        <div class="d-flex justify-content-end mb-2">
          <b-button variant="primary" size="sm" :disabled="savingGroupSelection" @click="saveGroupSelection">
            {{ savingGroupSelection ? 'Saving...' : 'Save' }}
          </b-button>
        </div>
        <table class="table table-striped table-bordered">
          <thead class="thead-dark">
            <tr>
              <th style="width:40px">
                <b-form-checkbox v-model="allGroupStudentsSelected" />
              </th>
              <th>Roll Number</th>
              <th>Register No</th>
              <th>Class Code</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="student in modalGroupStudents"
              :key="student.institution_user_id"
            >
              <td>
                <b-form-checkbox v-model="student.selected" />
              </td>
              <td>{{ student.roll_number || 'RN' }}</td>
              <td>{{ student.register_no || '—' }}</td>
              <td>{{ student.class_codes.filter(Boolean).join(', ') || '—' }}</td>
              <td>{{ student.first_name || '' }} {{ student.last_name || '' }}</td>
              <td>{{ student.email }}</td>
              <td>{{ student.mobile }}</td>
            </tr>
          </tbody>
        </table>
        <div class="d-flex justify-content-end">
          <b-button variant="primary" size="sm" :disabled="savingGroupSelection" @click="saveGroupSelection">
            {{ savingGroupSelection ? 'Saving...' : 'Save' }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Read-only enrolled roster (Enrolled Students / Total Enrolled Students) -->
    <b-modal id="coeEnrolledStudentsModal" :title="modalTitle" hide-footer centered size="lg">
      <div v-if="!modalEnrolledStudents.length" class="text-muted small py-2">No students enrolled.</div>
      <table v-else class="table table-striped table-bordered">
        <thead class="thead-dark">
          <tr>
            <th>Roll Number</th>
            <th>Register No</th>
            <th>Class Code</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="student in modalEnrolledStudents"
            :key="student.institution_user_id + '-' + (student.course_id || 0)"
          >
            <td>{{ student.roll_number || 'RN' }}</td>
            <td>{{ student.register_no || '—' }}</td>
            <td>{{ (student.course_codes || [student.class_code]).filter(Boolean).join(', ') || '—' }}</td>
            <td>{{ student.first_name || '' }} {{ student.last_name || '' }}</td>
            <td>{{ student.email }}</td>
            <td>{{ student.mobile }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Editable per-offering selection (Exam Students) -->
    <b-modal id="coeAddStudentsModal" :title="modalTitle" hide-footer centered size="lg">
      <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>
      <div v-if="!modalCourseStudents.length" class="text-muted small py-2">No students enrolled in this offering.</div>
      <template v-else>
        <table class="table table-striped table-bordered">
          <thead class="thead-dark">
            <tr>
              <th style="width:40px"></th>
              <th>Roll Number</th>
              <th>Register No</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="student in modalCourseStudents" :key="student.institution_user_id">
              <td>
                <b-form-checkbox v-model="student.selected" />
              </td>
              <td>{{ student.roll_number || 'RN' }}</td>
              <td>{{ student.register_no || '—' }}</td>
              <td>{{ student.first_name || '' }} {{ student.last_name || '' }}</td>
              <td>{{ student.email }}</td>
              <td>{{ student.mobile }}</td>
            </tr>
          </tbody>
        </table>
        <div class="d-flex justify-content-end">
          <b-button variant="primary" size="sm" :disabled="savingSelection" @click="saveSelectedStudents">
            {{ savingSelection ? 'Saving...' : 'Save' }}
          </b-button>
        </div>
      </template>
    </b-modal>

  <b-modal id="coeQpFacultyModal" :title="modalQpTitle" hide-footer centered size="md">
      <div v-if="errorMessage" class="text-danger small mb-2">
        {{ errorMessage }}
      </div>

      <div v-if="!modalQpFacultyList.length" class="text-muted small py-2">
        No faculties found.
      </div>

      <template v-else>
        <div class="d-flex justify-content-start mb-2">
          <b-button variant="primary" size="sm" @click="openAddFacultyModal">
            Add Faculty
          </b-button>
        </div>

        <div style="max-height: 60vh; overflow-y: auto;">
          <table class="table table-striped table-bordered">
            <thead class="thead-dark">
              <tr>
                <th>Name</th>
                <th style="width: 80px">Remove</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="faculty in modalQpFacultyList" :key="faculty.id" >
                <td v-if="faculty.selected">{{ faculty.name }}</td>
                <td v-if="faculty.selected" class="align-middle text-center">
                  <span class="btn p-0 m-0 text-danger" @click="removeFaculty(faculty)">
                    <i class="fas fa-trash"></i>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </b-modal>

    <b-modal id="coeAddQpFacultyModal" title="Add Faculty" hide-footer centered size="md">
      <b-form @submit.prevent="addQpFaculties">
        <b-form-group>
          <multiselect v-model="selectedQpFaculties" placeholder="Add Faculty" label="name" track-by="id"
            :options="availableQpFaculties" :multiple="true" :close-on-select="false" :clear-on-select="false" />
        </b-form-group>

        <div class="d-flex justify-content-between">
          <b-button size="sm" variant="primary" type="submit"
            :disabled="savingQpFaculties || !selectedQpFaculties.length">
            {{ savingQpFaculties ? "Please wait..." : "Save" }}
          </b-button>

          <b-button size="sm" variant="danger" type="button" @click="$bvModal.hide('coeAddQpFacultyModal')"
            :disabled="savingQpFaculties">
            Cancel
          </b-button>
        </div>
      </b-form>
    </b-modal>
    
   <b-modal id="coeEvaluationModal" title="Evaluation Assignment" hide-footer centered size="md">
      <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>

      <p class="text-muted small mb-3">
        Evaluation Type: <strong>{{ modalEvaluationType === 'double' ? 'Double' : 'Single' }}</strong>
        (set for the whole exam when it was created)
      </p>

      <b-form-group label="Evaluator 1">
        <div class="mb-2">
          <b-button variant="primary" size="sm" @click="openAddEvaluatorModal(1)">
            Add Evaluator
          </b-button>
        </div>

        <div style="max-height: 30vh; overflow-y: auto;">
          <table class="table table-striped table-bordered">
            <thead class="thead-dark">
              <tr>
                <th>Name</th>
                <th style="width: 80px">Remove</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="faculty in modalEvaluator1List" :key="faculty.id">
                <td v-if="faculty.selected">{{ faculty.name }}</td>
                <td v-if="faculty.selected" class="align-middle text-center">
                  <span class="btn p-0 m-0 text-danger" @click="removeEvaluator(faculty, 1)">
                    <i class="fas fa-trash"></i>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-form-group>

      <b-form-group v-if="modalEvaluationType === 'double'" label="Evaluator 2">
        <div class="mb-2">
          <b-button variant="primary" size="sm" @click="openAddEvaluatorModal(2)">
            Add Evaluator
          </b-button>
        </div>

        <div style="max-height: 30vh; overflow-y: auto;">
          <table class="table table-striped table-bordered">
            <thead class="thead-dark">
              <tr>
                <th>Name</th>
                <th style="width: 80px">Remove</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="faculty in modalEvaluator2List" :key="faculty.id">
                <td v-if="faculty.selected">{{ faculty.name }}</td>
                <td v-if="faculty.selected" class="align-middle text-center">
                  <span class="btn p-0 m-0 text-danger" @click="removeEvaluator(faculty, 2)">
                    <i class="fas fa-trash"></i>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-form-group>
    </b-modal>

    <b-modal id="coeAddEvaluatorModal" title="Add Evaluator" hide-footer centered size="md">
      <b-form @submit.prevent="addEvaluators">
        <b-form-group>
          <multiselect v-model="selectedEvaluators" placeholder="Add Faculty" label="name" track-by="id"
            :options="availableEvaluators" :multiple="true" :close-on-select="false" :clear-on-select="false" />
        </b-form-group>

        <div class="d-flex justify-content-between">
          <b-button size="sm" variant="primary" type="submit" :disabled="savingEvaluator || !selectedEvaluators.length">
            {{ savingEvaluator ? 'Please wait...' : 'Save' }}
          </b-button>

          <b-button size="sm" variant="danger" type="button" @click="$bvModal.hide('coeAddEvaluatorModal')"
            :disabled="savingEvaluator">
            Cancel
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Assign exam students to their evaluator(s), per evaluation round -->
    <b-modal id="coeEvaluatorStudentsModal" title="Assign Students to Evaluators" hide-footer centered size="lg">
      <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>
      <div v-if="!modalRound1List.length" class="text-muted small py-2">No exam students selected for this group.</div>
      <template v-else>
        <div class="d-flex justify-content-end mb-2">
          <b-button variant="primary" size="sm" :disabled="savingEvaluatorStudents" @click="saveEvaluatorStudents">
            {{ savingEvaluatorStudents ? 'Saving...' : 'Save' }}
          </b-button>
        </div>

        <div v-if="!modalRound1Pool.length" class="text-muted small mb-2">
          No evaluators assigned for Round 1 yet — assign evaluators first.
        </div>
        <div v-else class="mb-3">
          <div class="font-weight-bold mb-1">Round 1</div>
          <div class="mb-2">
            <b-badge v-for="item in evaluatorStudentCounts(modalRound1Pool, modalRound1List)" :key="item.id"
              :variant="item.id ? 'info' : 'secondary'" class="mr-1 mb-1 p-1">
              {{ item.name }}: {{ item.count }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center mb-2" style="gap: 8px;">
            <b-form-select
              v-model="modalRound1BulkEvaluatorId"
              :options="[{ value: null, text: 'Select Evaluator' }, ...modalRound1Pool.map(e => ({ value: e.id, text: e.name }))]"
              style="max-width: 260px;"
            />
            <b-button size="sm" variant="primary" :disabled="!modalRound1BulkEvaluatorId" @click="bulkAssignRound(1)">
              Assign to Selected
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="bulkUnassignRound(1)">Unassign Selected</b-button>
          </div>
          <div class="d-flex align-items-center mb-2" style="gap: 8px;">
            <b-form-input
              v-model="modalRound1RangeInput"
              size="sm"
              placeholder="Select by Sl.No, e.g. 1-10, 15"
              style="max-width: 260px;"
              @keyup="onRangeKeyup($event, 1)"
            />
          </div>
          <div style="max-height: 40vh; overflow-y: auto;">
            <table class="table table-striped table-bordered mb-0">
              <thead class="thead-dark">
                <tr>
                  <th style="width:40px">
                    <b-form-checkbox v-model="allRound1Selected" />
                  </th>
                  <th style="width:60px">Sl.No</th>
                  <th>Roll Number</th>
                  <th>Register No</th>
                  <th>Name</th>
                  <th style="width:200px">Evaluator</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(student, index) in modalRound1List" :key="student.student_id">
                  <td><b-form-checkbox v-model="student.checked" /></td>
                  <td>{{ index + 1 }}</td>
                  <td>{{ student.roll_number || 'RN' }}</td>
                  <td>{{ student.register_no || '—' }}</td>
                  <td>{{ student.name }}</td>
                  <td>{{ evaluatorName(modalRound1Pool, student.evaluator_id) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <template v-if="modalEvaluatorStudentsType === 'double'">
          <div v-if="!modalRound2Pool.length" class="text-muted small mb-2">
            No evaluators assigned for Round 2 yet — assign evaluators first.
          </div>
          <div v-else>
            <div class="font-weight-bold mb-1">Round 2</div>
            <div class="mb-2">
              <b-badge v-for="item in evaluatorStudentCounts(modalRound2Pool, modalRound2List)" :key="item.id"
                :variant="item.id ? 'info' : 'secondary'" class="mr-1 mb-1 p-1">
                {{ item.name }}: {{ item.count }}
              </b-badge>
            </div>
            <div class="d-flex align-items-center mb-2" style="gap: 8px;">
              <b-form-select
                v-model="modalRound2BulkEvaluatorId"
                :options="[{ value: null, text: 'Select Evaluator' }, ...modalRound2Pool.map(e => ({ value: e.id, text: e.name }))]"
                style="max-width: 260px;"
              />
              <b-button size="sm" variant="primary" :disabled="!modalRound2BulkEvaluatorId" @click="bulkAssignRound(2)">
                Assign to Selected
              </b-button>
              <b-button size="sm" variant="outline-secondary" @click="bulkUnassignRound(2)">Unassign Selected</b-button>
            </div>
            <div class="d-flex align-items-center mb-2" style="gap: 8px;">
              <b-form-input
                v-model="modalRound2RangeInput"
                size="sm"
                placeholder="Select by Sl.No, e.g. 1-10, 15"
                style="max-width: 260px;"
                @keyup="onRangeKeyup($event, 2)"
              />
            </div>
            <div style="max-height: 40vh; overflow-y: auto;">
              <table class="table table-striped table-bordered mb-0">
                <thead class="thead-dark">
                  <tr>
                    <th style="width:40px">
                      <b-form-checkbox v-model="allRound2Selected" />
                    </th>
                    <th style="width:60px">Sl.No</th>
                    <th>Roll Number</th>
                    <th>Register No</th>
                    <th>Name</th>
                    <th style="width:200px">Evaluator</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(student, index) in modalRound2List" :key="student.student_id">
                    <td><b-form-checkbox v-model="student.checked" /></td>
                    <td>{{ index + 1 }}</td>
                    <td>{{ student.roll_number || 'RN' }}</td>
                    <td>{{ student.register_no || '—' }}</td>
                    <td>{{ student.name }}</td>
                    <td>{{ evaluatorName(modalRound2Pool, student.evaluator_id) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </template>
    </b-modal>

    <!-- Date and time of exam, for every course in this group -->
    <b-modal id="coeExamDatesModal" title="Date of Exam" hide-footer centered size="lg">
      <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>
      <div v-if="!modalExamDateList.length" class="text-muted small py-2">No courses in this group.</div>
      <template v-else>
        <div class="d-flex justify-content-end mb-2">
          <b-button variant="primary" size="sm" :disabled="savingExamDates" @click="saveExamDates">
            {{ savingExamDates ? 'Saving...' : 'Save' }}
          </b-button>
        </div>

        <div style="max-height: 60vh; overflow-y: auto;">
          <table class="table table-striped table-bordered mb-0">
            <thead class="thead-dark">
              <tr>
                <th style="width:130px">Course Code</th>
                <th>Course Name</th>
                <th style="width:160px">Date</th>
                <th style="width:130px">Start Time</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in modalExamDateList" :key="row.code">
                <td class="align-middle font-weight-bold">{{ row.code }}</td>
                <td class="align-middle">{{ row.name }}</td>
                <td><b-form-input type="date" size="sm" v-model="row.exam_date" /></td>
                <td><b-form-input type="time" size="sm" v-model="row.start_time" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </b-modal>

    <!-- Assign exam students to question paper sets (A/B/C) -->
    <b-modal id="coeQpSetStudentsModal" title="Assign to Sets" hide-footer centered size="lg">
      <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>
      <div v-if="!modalStudentSetList.length" class="text-muted small py-2">No exam students selected for this group.</div>
      <template v-else>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="d-flex flex-wrap" style="gap: 6px;">
            <b-button
              v-for="opt in [{ value: null, text: 'Not assigned' }, ...qpSetLabelOptions]"
              :key="opt.text"
              variant="outline-secondary"
              size="sm"
              @click="modalStudentSetList.forEach(s => { s.set = opt.value; })"
            >
              Select All {{ opt.text }}
            </b-button>
          </div>
          <b-button variant="primary" size="sm" :disabled="savingStudentSets" @click="saveQpSetStudents">
            {{ savingStudentSets ? 'Saving...' : 'Save' }}
          </b-button>
        </div>

        <div style="max-height: 60vh; overflow-y: auto;">
          <table class="table table-striped table-bordered mb-0">
            <thead class="thead-dark">
              <tr>
                <th>Roll Number</th>
                <th>Register No</th>
                <th>Name</th>
                <th>Set</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="student in modalStudentSetList" :key="student.institution_user_id">
                <td>{{ student.roll_number || 'RN' }}</td>
                <td>{{ student.register_no || '—' }}</td>
                <td>{{ student.name }}</td>
                <td>
                  <b-form-radio-group
                    v-model="student.set"
                    :options="[{ value: null, text: 'Not assigned' }, ...qpSetLabelOptions]"
                    :name="`qp-set-${student.institution_user_id}`"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </b-modal>

    <!-- Eligibility wizard (progressive: Attendance Check -> steps to be defined) -->
    <b-modal id="coeEligibilityWizardModal" title="Exam Eligibility Check" hide-footer centered size="xl">
      <div v-if="errorMessage" class="text-danger small mb-2 px-3 pt-2">{{ errorMessage }}</div>
      <b-tabs v-model="eligibilityWizardTabIndex" class="coe-eligibility-tabs">
        <b-tab title="1. Eligibility Check" active>
          <div class="p-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <p class="text-muted small mb-0">
                Check eligibility for all selected exam students across every course in this group.
              </p>
              <b-button
                variant="primary"
                size="sm"
                :disabled="runningGroupEligibilityCheck"
                @click="runGroupEligibilityCheck"
              >
                <b-spinner small v-if="runningGroupEligibilityCheck" class="mr-1" />
                <i v-else class="fas fa-user-check mr-1"></i>
                {{ runningGroupEligibilityCheck ? 'Checking...' : (groupEligibilityChecked ? 'Recheck Eligibility' : 'Check Eligibility') }}
              </b-button>
            </div>

            <div class="d-flex justify-content-end mb-3">
              <b-button
                variant="primary"
                size="sm"
                :disabled="!groupEligibilityChecked"
                @click="goToStep2"
              >
                Next
              </b-button>
            </div>

            <div v-if="!groupEligibilityChecked" class="text-muted small text-center py-4">
              Click "Check Eligibility" to compute attendance-based eligibility for exam students.
            </div>

            <table v-else class="table table-striped table-bordered">
              <thead class="thead-dark">
                <tr>
                  <th style="width:130px">Class Code</th>
                  <th>Course Name</th>
                  <th style="width:220px">Eligibility Summary</th>
                  <th style="width:140px">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="course in groupEligibilityCourses" :key="course.id">
                  <td>{{ course.class_code }}</td>
                  <td>{{ course.name }}</td>
                  <td>
                    <div>
                      Eligible:
                      <a href="#" @click.prevent="showEligibilityStudentsList(course, 'eligible')">{{ course.eligibleCount }}</a>
                    </div>
                    <div>
                      Eligible with Condonation:
                      <a href="#" @click.prevent="showEligibilityStudentsList(course, 'condonation')">{{ course.condonationCount }}</a>
                    </div>
                    <div>
                      Ineligible:
                      <a href="#" @click.prevent="showEligibilityStudentsList(course, 'ineligible')">{{ course.ineligibleCount }}</a>
                    </div>
                  </td>
                  <td>
                    <b-badge :variant="courseEligibilityBadgeVariant(course.status)" pill>
                      {{ courseEligibilityStatusLabel(course.status) }}
                    </b-badge>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- <div v-if="groupEligibilityChecked && hasIneligibleCourse" class="text-danger small mb-2 text-right">
              One or more courses have ineligible students. Resolve these before proceeding.
            </div> -->
          </div>
        </b-tab>

        <b-tab title="2. Exam Registration" :disabled="!groupEligibilityChecked">
          <div class="p-3">
            <div class="d-flex justify-content-between mb-3">
              <b-button variant="outline-secondary" size="sm" @click="eligibilityWizardTabIndex = 0">Back</b-button>
              <b-button
                variant="primary"
                size="sm"
                :disabled="!step3Students.length"
                @click="eligibilityWizardTabIndex = 2"
              >
                Next
              </b-button>
            </div>
            <div v-if="!step3Students.length" class="text-danger small mb-3 text-right">
              Select at least one student to proceed.
            </div>

            <div class="d-flex mb-3" style="gap: 24px;">
              <b-form-checkbox :checked="allStep2EligibleSelected" @change="setAllStep2Selected('eligible', $event)">Select All Eligible</b-form-checkbox>
              <b-form-checkbox :checked="allStep2CondonationSelected" @change="setAllStep2Selected('condonation', $event)">Select All Eligible with Condonation</b-form-checkbox>
            </div>

            <div v-if="!step2Students.length" class="text-muted small text-center py-4">
              No eligible students to display.
            </div>

            <table v-else class="table table-striped table-bordered">
              <thead class="thead-dark">
                <tr>
                  <th style="width:40px"></th>
                  <th style="width:110px">Roll Number</th>
                  <th style="width:110px">Register No</th>
                  <th>Student</th>
                  <th style="width:120px">Class Code</th>
                  <th>Courses</th>
                  <th style="width:160px">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="student in step2Students" :key="student.institution_user_id">
                  <td><b-form-checkbox v-model="student.selected" /></td>
                  <td>{{ student.roll_number || 'RN' }}</td>
                  <td>{{ student.register_no || '—' }}</td>
                  <td>{{ student.first_name || '' }} {{ student.last_name || '' }}</td>
                  <td>{{ student.class_codes.filter(Boolean).join(', ') || '—' }}</td>
                  <td>
                    <b-badge
                      v-for="(course, ci) in student.courses"
                      :key="ci"
                      variant="light"
                      class="mr-1 mb-1 course-badge"
                    >
                      {{ course }}
                      <i
                        class="fas fa-times ml-1"
                        style="cursor:pointer;"
                        v-b-tooltip.hover
                        title="Remove course"
                        @click="removeStudentCourse(student, ci)"
                      ></i>
                    </b-badge>
                  </td>
                  <td>
                    <b-badge :variant="student.status === 'condonation' ? 'warning' : 'success'" pill>
                      {{ student.status === 'condonation' ? 'Eligible (Condonation)' : 'Eligible' }}
                    </b-badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <b-tab title="3. Issue Hall Ticket" :disabled="!groupEligibilityChecked">
          <div class="p-3">
            <div class="d-flex justify-content-between mb-3">
              <b-button variant="outline-secondary" size="sm" @click="eligibilityWizardTabIndex = 1">Back</b-button>
              <b-button
                variant="primary"
                size="sm"
                :disabled="finishingHallTickets"
                @click="finishHallTicketIssuance"
              >
                <b-spinner small v-if="finishingHallTickets" class="mr-1" />
                {{ finishingHallTickets ? 'Saving...' : 'Finish' }}
              </b-button>
            </div>

            <div v-if="!step3Students.length" class="text-muted small text-center py-4">
              No students selected in the previous step.
            </div>

            <table v-else class="table table-striped table-bordered">
              <thead class="thead-dark">
                <tr>
                  <th style="width:110px">Roll Number</th>
                  <th style="width:110px">Register No</th>
                  <th>Student</th>
                  <th style="width:120px">Class Code</th>
                  <th>Courses</th>
                  <th style="width:150px">Hall Ticket</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="student in step3Students" :key="student.institution_user_id">
                  <td>{{ student.roll_number || 'RN' }}</td>
                  <td>{{ student.register_no || '—' }}</td>
                  <td>{{ student.first_name || '' }} {{ student.last_name || '' }}</td>
                  <td>{{ student.class_codes.filter(Boolean).join(', ') || '—' }}</td>
                  <td>{{ student.courses.join(', ') }}</td>
                  <td>
                    <b-button
                      variant="primary"
                      size="sm"
                      :disabled="generatingHallTicketFor === student.institution_user_id"
                      @click="generateHallTicketForStudent(student)"
                    >
                      <b-spinner small v-if="generatingHallTicketFor === student.institution_user_id" class="mr-1" />
                      {{ generatingHallTicketFor === student.institution_user_id ? 'Generating...' : 'Generate' }}
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>

    <!-- Eligibility category student list -->
    <b-modal id="coeEligibilityStudentsListModal" :title="eligibilityStudentsListTitle" hide-footer centered size="md">
      <div v-if="!eligibilityStudentsList.length" class="text-muted small py-2">No students in this category.</div>
      <table v-else class="table table-striped table-bordered">
        <thead class="thead-dark">
          <tr>
            <th>Roll Number</th>
            <th>Register No</th>
            <th>Name</th>
            <th>Attendance %</th>
            <th>Internal Marks %</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="student in eligibilityStudentsList" :key="student.institution_user_id">
            <td>{{ student.roll_number || 'RN' }}</td>
            <td>{{ student.register_no || '—' }}</td>
            <td>{{ student.first_name || '' }} {{ student.last_name || '' }}</td>
            <td>{{ student.attendance_percentage !== null && student.attendance_percentage !== undefined ? student.attendance_percentage + '%' : '—' }}</td>
            <td>{{ student.internal_marks_percentage !== null && student.internal_marks_percentage !== undefined ? student.internal_marks_percentage + '%' : '—' }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <DashboardModal
      id="questionPaperModal"
      parent="Question Paper"
      @close="closeQuestionPaperModal"
    >
      <ManageExamQuestions
        v-if="selectedExam"
        :exam="selectedExam"
        :programme_config_group_id="selectedProgrammeConfigGroupId"
        :readOnly="selectedReadOnly"
      />
    </DashboardModal>
  </div>
</template>

<script>
import DashboardModal from "../DashboardModal.vue";
import ManageExamQuestions from "../../examination/manageExamQuestions.vue";
import ExamQuestionLogs from "../../examination/ExamQuestionLogs.vue";
const QP_SET_LABELS = ["A", "B", "C"];

export default {
  name: "CoeCourseGroupDetail",

  components: {
      DashboardModal,
      ManageExamQuestions,
      ExamQuestionLogs
  },

  props: {
    groupId: { type: Number, required: true },
    examId: { type: Number, required: true },
  },

  data() {
    return {
      selectedEvaluators: [],
      availableEvaluators: [],
      selectedEvaluatorNumber: null,
      savingEvaluator: false,
      selectedQpFaculties: [],
      availableQpFaculties: [],
      savingQpFaculties: false,
      selectedExam: null,
      selectedProgrammeConfigGroupId: null,
      selectedReadOnly: false,
      fetchData: false,
      errorMessage: "",
      stickyHeaderOffset: 0,
      group: {},
      enrolledByCourse: {},
      selectedByCourse: {},
      loadingCounts: false,
      modalTitle: "",
      modalEnrolledStudents: [],
      modalGroupStudents: [],
      modalGroupCourseIds: [],
      savingGroupSelection: false,
      modalCourseId: null,
      modalCourseStudents: [],
      savingSelection: false,
      eligibilityWizardTabIndex: 0,
      runningGroupEligibilityCheck: false,
      groupEligibilityChecked: false,
      groupEligibilityCourses: [],
      eligibilityStudentsList: [],
      eligibilityStudentsListTitle: "",
      step2Students: [],
      generatingHallTicketFor: null,
      finishingHallTickets: false,
      faculties: [],
      qpSetsByGroup: {},
      qpStatusByGroup: {},
      // published result locks the QP edit date and override
      resultPublished: false,
      loadingQpSets: false,
      modalQpTitle: "",
      questionLogExamId: null,
      questionLogTitle: "",
      modalQpGroupCode: null,
      modalQpSetLabel: "",
      modalQpFacultyList: [],
      evaluationsByGroup: {},
      loadingEvaluations: false,
      modalEvaluationGroupCode: null,
      modalEvaluationType: "single",
      modalEvaluator1List: [],
      modalEvaluator2List: [],
      savingEvaluation: false,
      qpSetStudentsByGroup: {},
      loadingQpSetStudents: false,
      modalStudentSetGroupCode: null,
      modalStudentSetList: [],
      savingStudentSets: false,
      evaluatorStudentsByGroup: {},
      loadingEvaluatorStudents: false,
      modalEvaluatorStudentsGroupCode: null,
      modalEvaluatorStudentsType: "single",
      modalRound1Pool: [],
      modalRound2Pool: [],
      modalRound1List: [],
      modalRound2List: [],
      modalRound1BulkEvaluatorId: null,
      modalRound2BulkEvaluatorId: null,
      modalRound1RangeInput: "",
      modalRound2RangeInput: "",
      examDatesByGroup: {},
      loadingExamDates: false,
      modalExamDateList: [],
      savingExamDates: false,
      savingEvaluatorStudents: false,
    };
  },

  computed: {
    qpSetLabelOptions() {
      return QP_SET_LABELS.map(label => ({ value: label, text: `Set ${label}` }));
    },
    displayGroups() {
      const groupedCourses = this.group.grouped_courses || [];
      let shadeToggle = false;
      // a grouped course whose courses were all deleted has nothing to show, and every row reads courses[0]
      return groupedCourses.filter(gc => (gc.courses || []).length).map(gc => {
        shadeToggle = !shadeToggle;
        const courses = (gc.courses || []).map(c => {
          const classCode = c.usersubgroup ? c.usersubgroup.code : null;
          const enrolled = this.enrolledByCourse[c.id] || [];
          const selectedIds = this.selectedByCourse[c.id] || [];
          const selectedStudents = enrolled
            .filter(s => selectedIds.includes(s.institution_user_id))
            .map(s => ({ ...s, course_codes: [classCode] }));

          return {
            id:           c.id,
            name:         c.name,
            class_code:   classCode,
            academic_year: c.academic_year,
            academic_semester: c.academic_semester,
            program_name: c.usersubgroup && c.usersubgroup.usergroup
              ? c.usersubgroup.usergroup.name
              : null,
            enrolled,
            selectedIds,
            selectedStudents,
          };
        });

        const mergeByCourseCode = (list) => {
          const merged = new Map();
          list.forEach(({ student, classCode }) => {
            if (merged.has(student.institution_user_id)) {
              const existing = merged.get(student.institution_user_id);
              if (!existing.course_codes.includes(classCode)) {
                existing.course_codes.push(classCode);
              }
            } else {
              merged.set(student.institution_user_id, { ...student, course_codes: [classCode] });
            }
          });
          return Array.from(merged.values());
        };

        const selectedEntries = [];
        const enrolledEntries = [];
        courses.forEach(c => {
          c.selectedStudents.forEach(s => selectedEntries.push({ student: s, classCode: c.class_code }));
          c.enrolled.forEach(s => enrolledEntries.push({ student: s, classCode: c.class_code }));
        });

        const totalSelectedStudents = mergeByCourseCode(selectedEntries);

        const studentSetByStudentId = this.qpSetStudentsByGroup[gc.code] || {};
        const studentCounts = { A: 0, B: 0, C: 0 };
        totalSelectedStudents.forEach(s => {
          const set = studentSetByStudentId[s.institution_user_id];
          if (set && studentCounts[set] !== undefined) studentCounts[set]++;
        });

        const savedSets = this.qpSetsByGroup[gc.code] || {};
        const savedStatus = this.qpStatusByGroup[gc.code] || {};
        const qpSets = QP_SET_LABELS.map(label => {
          const saved = savedSets[label] || {};
          const status = savedStatus[label] || {};
          return {
            label,
            faculty_ids: saved.faculty_ids || [],
            faculty_names: saved.faculty_names || [],
            exam_id: status.exam_id || null,
            submitted_at: status.submitted_at || null,
            submitted_by_name: status.submitted_by_name || null,
            edit_until: status.edit_until || null,
            allow_edit_after_deadline: !!status.allow_edit_after_deadline,
            log_count: status.log_count || 0,
            course_id: status.course_id || null,
            usergroup_id: status.usergroup_id || null,
            config_group_id: status.config_group_id || null,
            student_count: studentCounts[label],
          };
        });

        const savedEvaluation = this.evaluationsByGroup[gc.code] || {};
        const evaluation = {
          evaluation_type: savedEvaluation.evaluation_type || "single",
          evaluator_1: savedEvaluation.evaluator_1 || [],
          evaluator_2: savedEvaluation.evaluator_2 || [],
        };

        return {
          code:        gc.code,
          nameMismatch: gc.name_mismatch_flag,
          sourceNames:  gc.source_names || [],
          shaded:       shadeToggle,
          courses,
          qpSets,
          evaluation,
          totalSelectedStudents,
          totalEnrolledStudents: mergeByCourseCode(enrolledEntries),
        };
      });
    },

    totalOfferings() {
      return this.group.grouped_courses.length;
    },

    allGroupStudentsSelected: {
      get() {
        return this.modalGroupStudents.length > 0 && this.modalGroupStudents.every(s => s.selected);
      },
      set(value) {
        this.modalGroupStudents.forEach(s => { s.selected = value; });
      },
    },

    allRound1Selected: {
      get() {
        return this.modalRound1List.length > 0 && this.modalRound1List.every(s => s.checked);
      },
      set(value) {
        this.modalRound1List.forEach(s => { s.checked = value; });
      },
    },

    allRound2Selected: {
      get() {
        return this.modalRound2List.length > 0 && this.modalRound2List.every(s => s.checked);
      },
      set(value) {
        this.modalRound2List.forEach(s => { s.checked = value; });
      },
    },

    hasIneligibleCourse() {
      return this.groupEligibilityCourses.some(c => c.status === "ineligible");
    },

    allStep2EligibleSelected() {
      const students = this.step2Students.filter(s => s.status === "eligible");
      return students.length > 0 && students.every(s => s.selected);
    },

    allStep2CondonationSelected() {
      const students = this.step2Students.filter(s => s.status === "condonation");
      return students.length > 0 && students.every(s => s.selected);
    },

    step3Students() {
      return this.step2Students.filter(s => s.selected);
    },
  },

  async mounted() {
    await this.fetchGroupDetail();
    this.fetchFaculties();
    this.fetchQpSets();
    this.fetchEvaluations();
    this.fetchQpSetStudents();
    this.fetchEvaluatorStudents();
    this.fetchExamDates();
  },

  updated() {
    this.measureStickyHeader();
  },

  methods: {
    // Second header row sticks right below the first one, whose height is only known
    // once the table is rendered.
    measureStickyHeader() {
      const headerRow = this.$refs.groupHeaderRow;
      // Less the cell borders, so the two rows sit flush instead of leaving a gap.
      if (headerRow) this.stickyHeaderOffset = headerRow.offsetHeight - 2;
    },

    openAddEvaluatorModal(evaluatorNumber) {
      this.selectedEvaluatorNumber = evaluatorNumber;

      const list = evaluatorNumber === 1
        ? this.modalEvaluator1List
        : this.modalEvaluator2List;

      this.selectedEvaluators = [];

      this.availableEvaluators = list.filter(
        faculty => !faculty.selected
      );

      this.$bvModal.show('coeAddEvaluatorModal');
    },

    async addEvaluators() {
      this.savingEvaluator = true;

      const list = this.selectedEvaluatorNumber === 1
        ? this.modalEvaluator1List
        : this.modalEvaluator2List;

      this.selectedEvaluators.forEach(faculty => {
        const existing = list.find(item => item.id === faculty.id);

        if (existing) {
          existing.selected = true;
        }
      });

      await this.saveEvaluationAssignment(false);

      this.savingEvaluator = false;
      this.selectedEvaluators = [];
      this.$bvModal.hide('coeAddEvaluatorModal');
    },

    removeEvaluator(faculty) {
      faculty.selected = false;
      this.saveEvaluationAssignment();
    },
    openAddFacultyModal() {
      this.selectedQpFaculties = [];

      this.availableQpFaculties = this.modalQpFacultyList.filter(
        faculty => !faculty.selected
      );

      this.$bvModal.show("coeAddQpFacultyModal");
    },

    async addQpFaculties() {
      this.savingQpFaculties = true;

      this.selectedQpFaculties.forEach(faculty => {
        const existing = this.modalQpFacultyList.find(
          item => item.id === faculty.id
        );

        if (existing) {
          existing.selected = true;
        }
      });

      await this.saveQpFaculties(false);

      this.savingQpFaculties = false;
      this.selectedQpFaculties = [];
      this.$bvModal.hide("coeAddQpFacultyModal");
    },


    async fetchGroupDetail() {
      this.fetchData = true;
      this.errorMessage = "";
      try {
        const res = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/course-groups/${this.groupId}`
        );
        this.group = res.data.data || {};
        await this.fetchExamStudents();
      } catch {
        this.errorMessage = "Failed to load course group details.";
      } finally {
        this.fetchData = false;
      }
    },

    async fetchExamStudents() {
      const courseIds = [];
      (this.group.grouped_courses || []).forEach(gc => {
        (gc.courses || []).forEach(c => courseIds.push(c.id));
      });
      if (!courseIds.length) return;

      this.loadingCounts = true;
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      await Promise.all(
        courseIds.map(id =>
          this.$axios
            .get(`${mainAPI}/end-sem-exams/${this.examId}/courses/${id}/students`)
            .then(res => {
              const data = res.data.data || {};
              this.$set(this.enrolledByCourse, id, data.students || []);
              this.$set(this.selectedByCourse, id, data.selected_ids || []);
            })
            .catch(() => {
              this.$set(this.enrolledByCourse, id, []);
              this.$set(this.selectedByCourse, id, []);
            })
        )
      );
      this.loadingCounts = false;
    },

    viewEnrolledStudents(course) {
      this.modalTitle = course.name + " - " + (course.class_code || "Offering") + " : Enrolled Students";
      this.modalEnrolledStudents = course.enrolled.map(s => ({ ...s, class_code: course.class_code }));
      this.$bvModal.show("coeEnrolledStudentsModal");
    },

    viewTotalEnrolledStudents(grp) {
      this.modalTitle = grp.courses[0].name + " - All Offerings : Enrolled Students";
      this.modalEnrolledStudents = grp.totalEnrolledStudents;
      this.$bvModal.show("coeEnrolledStudentsModal");
    },

    openGroupSelectionModal(grp) {
      this.modalTitle = grp.courses[0].name + " - All Offerings : Add Exam Students";

      const merged = new Map();
      grp.courses.forEach(c => {
        c.enrolled.forEach(s => {
          const isSelected = c.selectedIds.includes(s.institution_user_id);
          if (merged.has(s.institution_user_id)) {
            const existing = merged.get(s.institution_user_id);
            existing.course_ids.push(c.id);
            if (isSelected && !existing.class_codes.includes(c.class_code)) {
              existing.class_codes.push(c.class_code);
            }
            existing.selected = existing.selected || isSelected;
          } else {
            merged.set(s.institution_user_id, {
              ...s,
              course_ids: [c.id],
              class_codes: isSelected ? [c.class_code] : [],
              selected: isSelected,
            });
          }
        });
      });
      this.modalGroupStudents = Array.from(merged.values());
      this.modalGroupCourseIds = grp.courses.map(c => c.id);
      this.errorMessage = "";
      this.$bvModal.show("coeGroupStudentsModal");
    },

    openEligibilityWizard() {
      this.eligibilityWizardTabIndex = 0;
      this.groupEligibilityChecked = false;
      this.groupEligibilityCourses = [];
      this.step2Students = [];
      this.errorMessage = "";
      this.$bvModal.show("coeEligibilityWizardModal");
    },

    goToStep2() {
      const merged = new Map();
      this.groupEligibilityCourses.forEach(course => {
        course.eligibleStudents.forEach(s => this.mergeStep2Student(merged, course, s, "eligible"));
        course.condonationStudents.forEach(s => this.mergeStep2Student(merged, course, s, "condonation"));
      });
      this.step2Students = Array.from(merged.values());
      this.eligibilityWizardTabIndex = 1;
    },

    mergeStep2Student(merged, course, student, status) {
      if (merged.has(student.institution_user_id)) {
        const existing = merged.get(student.institution_user_id);
        if (!existing.course_ids.includes(course.id)) {
          existing.courses.push(course.name);
          existing.course_ids.push(course.id);
        }
        if (course.class_code && !existing.class_codes.includes(course.class_code)) existing.class_codes.push(course.class_code);
        if (status === "condonation") existing.status = "condonation";
      } else {
        merged.set(student.institution_user_id, {
          institution_user_id: student.institution_user_id,
          roll_number: student.roll_number,
          register_no: student.register_no,
          first_name: student.first_name,
          last_name: student.last_name,
          courses: [course.name],
          course_ids: [course.id],
          class_codes: course.class_code ? [course.class_code] : [],
          status,
          selected: true,
        });
      }
    },

    removeStudentCourse(student, courseIndex) {
      this.errorMessage = "";
      if (student.courses.length <= 1) {
        this.errorMessage = "A student must have at least one course.";
        return;
      }
      student.courses.splice(courseIndex, 1);
      student.course_ids.splice(courseIndex, 1);
    },

    setAllStep2Selected(status, value) {
      this.step2Students.filter(s => s.status === status).forEach(s => { s.selected = value; });
    },

    async generateHallTicketForStudent(student) {
      this.generatingHallTicketFor = student.institution_user_id;
      this.errorMessage = "";
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const response = await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/students/${student.institution_user_id}/hall-ticket`,
          {
            course_ids: student.course_ids,
            roll_number: student.roll_number,
            class_code: student.class_codes.filter(Boolean).join(", "),
          },
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Hall_Ticket_${student.roll_number || student.institution_user_id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        this.errorMessage = "Failed to generate hall ticket.";
      } finally {
        this.generatingHallTicketFor = null;
      }
    },

    async finishHallTicketIssuance() {
      this.errorMessage = "";
      if (!this.step3Students.length) {
        this.$bvModal.hide("coeEligibilityWizardModal");
        return;
      }

      this.finishingHallTickets = true;
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/hall-tickets/bulk-issue`,
          {
            students: this.step3Students.map(s => ({
              institution_user_id: s.institution_user_id,
              roll_number: s.roll_number,
              class_code: s.class_codes.filter(Boolean).join(", "),
              course_ids: s.course_ids,
            })),
          }
        );
        this.$toast.success("Hall tickets recorded for selected students.", { duration: 3000 });
        this.$bvModal.hide("coeEligibilityWizardModal");
      } catch {
        this.errorMessage = "Failed to save hall ticket records.";
      } finally {
        this.finishingHallTickets = false;
      }
    },

    async runGroupEligibilityCheck() {
      this.errorMessage = "";
      const courses = this.displayGroups.flatMap(g => g.courses);
      const courseIds = courses.map(c => c.id);
      const studentIds = Array.from(new Set(courses.flatMap(c => c.selectedIds)));

      if (!studentIds.length) {
        this.errorMessage = "No exam students selected to check eligibility.";
        return;
      }

      this.runningGroupEligibilityCheck = true;
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const [attendanceRes, internalMarksRes] = await Promise.all([
          this.$axios.post(
            `${mainAPI}/end-sem-exams/${this.examId}/eligibility/attendance`,
            { course_ids: courseIds, student_ids: studentIds }
          ),
          this.$axios.post(
            `${mainAPI}/end-sem-exams/${this.examId}/eligibility/internal-marks`,
            { course_ids: courseIds, student_ids: studentIds }
          ),
        ]);
        // Attendance and internal marks are returned per course (data[courseId][studentId]) since they differ by subject.
        const attendanceResultsByCourse = attendanceRes.data.data || {};
        const internalMarksResultsByCourse = internalMarksRes.data.data || {};

        const buildStudentRow = (course, studentId, attendanceResult, internalMarksResult) => {
          const student = course.enrolled.find(s => s.institution_user_id === studentId) || {};
          return {
            institution_user_id: studentId,
            roll_number: student.roll_number,
            register_no: student.register_no,
            first_name: student.first_name,
            last_name: student.last_name,
            attendance_percentage: attendanceResult ? attendanceResult.attendance_percentage : null,
            internal_marks_percentage: internalMarksResult ? internalMarksResult.internal_marks_percentage : null,          };
        };

        // Overall eligibility = attendance eligible AND internal marks eligible, combined per student.
        this.groupEligibilityCourses = courses.map(c => {
          const total = c.selectedIds.length;
          const attendanceResults = attendanceResultsByCourse[c.id] || {};
          const internalMarksResults = internalMarksResultsByCourse[c.id] || {};

          const eligibleStudents = [];
          const condonationStudents = [];
          const ineligibleStudents = [];

          c.selectedIds.forEach(id => {
            const attendanceResult = attendanceResults[id] || {};
            const internalMarksResult = internalMarksResults[id] || {};
            const row = buildStudentRow(c, id, attendanceResult, internalMarksResult);

            const attendancePass = attendanceResult.status === "eligible" || attendanceResult.status === "not_configured";
            const attendanceCondonation = attendanceResult.status === "condonation";
            const internalMarksPass = internalMarksResult.status === "eligible" || internalMarksResult.status === "not_configured";

            if (!internalMarksPass || (!attendancePass && !attendanceCondonation)) {
              ineligibleStudents.push(row);
            } else if (attendanceCondonation) {
              condonationStudents.push(row);
            } else {
              eligibleStudents.push(row);
            }
          });

          return {
            id: c.id,
            name: c.name,
            class_code: c.class_code,
            total,
            eligibleCount: eligibleStudents.length,
            condonationCount: condonationStudents.length,
            ineligibleCount: ineligibleStudents.length,
            eligibleStudents,
            condonationStudents,
            ineligibleStudents,
            status: total === 0 ? "no_students" : (ineligibleStudents.length === 0 ? "eligible" : "ineligible"),
          };
        });
        this.groupEligibilityChecked = true;
      } catch {
        this.errorMessage = "Failed to check eligibility.";
      } finally {
        this.runningGroupEligibilityCheck = false;
      }
    },

    courseEligibilityBadgeVariant(status) {
      const variants = {
        eligible: "success",
        ineligible: "danger",
        no_students: "secondary",
      };
      return variants[status] || "secondary";
    },

    courseEligibilityStatusLabel(status) {
      const labels = {
        eligible: "Eligible",
        ineligible: "Ineligible",
        no_students: "No Students Selected",
      };
      return labels[status] || "—";
    },

    showEligibilityStudentsList(course, category) {
      const categories = {
        eligible: { title: `${course.name} - Eligible Students`, students: course.eligibleStudents },
        condonation: { title: `${course.name} - Eligible Students (via Condonation)`, students: course.condonationStudents },
        ineligible: { title: `${course.name} - Ineligible Students`, students: course.ineligibleStudents },
      };
      const config = categories[category];
      this.eligibilityStudentsListTitle = config.title;
      this.eligibilityStudentsList = config.students;
      this.$bvModal.show("coeEligibilityStudentsListModal");
    },

    async saveGroupSelection() {
      this.savingGroupSelection = true;
      this.errorMessage = "";
      const byCourse = {};
      this.modalGroupStudents.forEach(s => {
        s.course_ids.forEach(cid => {
          if (!byCourse[cid]) byCourse[cid] = [];
          if (s.selected) byCourse[cid].push(s.institution_user_id);
        });
      });
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        await Promise.all(
          Object.keys(byCourse).map(courseId =>
            this.$axios
              .post(`${mainAPI}/end-sem-exams/${this.examId}/courses/${courseId}/students`, {
                student_ids: byCourse[courseId],
              })
              .then(() => this.$set(this.selectedByCourse, courseId, byCourse[courseId]))
          )
        );
        this.$toast.success("Exam students updated.", { duration: 3000 });
        this.$bvModal.hide("coeGroupStudentsModal");
      } catch {
        this.errorMessage = "Failed to update exam students.";
      } finally {
        this.savingGroupSelection = false;
      }
    },

    async fetchFaculties() {
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        const res = await this.$axios.get(`${mainAPI}/institutionusers/faculties`);
        this.faculties = (res.data.data || res.data || []).map(f => ({
          id: f.id,
          name: [f.first_name, f.last_name].filter(Boolean).join(" "),
        }));
      } catch {
        this.errorMessage = "Failed to load faculties.";
      }
    },

    async fetchQpSets() {
      this.loadingQpSets = true;
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        const res = await this.$axios.get(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/question-paper-sets`
        );
        const records = res.data.data || [];
        const byGroup = {};
        records.forEach(r => {
          if (!byGroup[r.code]) byGroup[r.code] = {};
          if (!byGroup[r.code][r.set]) byGroup[r.code][r.set] = { faculty_ids: [], faculty_names: [] };
          byGroup[r.code][r.set].faculty_ids.push(r.faculty_id);
          byGroup[r.code][r.set].faculty_names.push(r.faculty_name);
        });
        this.qpSetsByGroup = byGroup;

        const qpSets = res.data.qp_sets || [];
        const statusByGroup = {};
        qpSets.forEach(r => {
          if (!statusByGroup[r.code]) statusByGroup[r.code] = {};
          statusByGroup[r.code][r.set] = {
            exam_id: r.exam_id,
            submitted_at: r.submitted_at,
            submitted_by_name: r.submitted_by_name,
            edit_until: r.edit_until,
            allow_edit_after_deadline: !!r.allow_edit_after_deadline,
            log_count: r.log_count || 0,
            course_id: r.course_id,
            usergroup_id: r.usergroup_id,
            config_group_id: r.config_group_id,
          };
        });
        this.qpStatusByGroup = statusByGroup;
        this.resultPublished = !!res.data.result_published;
      } catch {
        this.errorMessage = "Failed to load question paper set assignments.";
      } finally {
        this.loadingQpSets = false;
      }
    },

    async fetchEvaluations() {
      this.loadingEvaluations = true;
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        const res = await this.$axios.get(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/evaluations`
        );
        const byGroup = {};
        (res.data.data || []).forEach(r => {
          byGroup[r.code] = {
            evaluation_type: r.evaluation_type,
            evaluator_1: r.evaluator_1,
            evaluator_2: r.evaluator_2,
          };
        });
        this.evaluationsByGroup = byGroup;
      } catch {
        this.errorMessage = "Failed to load evaluation assignments.";
      } finally {
        this.loadingEvaluations = false;
      }
    },

    openEvaluationModal(grp) {
      this.modalEvaluationGroupCode = grp.code;
      this.modalEvaluationType = grp.evaluation.evaluation_type;
      const selected1 = grp.evaluation.evaluator_1.map(e => e.id);
      const selected2 = grp.evaluation.evaluator_2.map(e => e.id);
      this.modalEvaluator1List = this.faculties.map(f => ({ ...f, selected: selected1.includes(f.id) }));
      this.modalEvaluator2List = this.faculties.map(f => ({ ...f, selected: selected2.includes(f.id) }));
      this.errorMessage = "";
      this.$bvModal.show("coeEvaluationModal");
    },

    async saveEvaluationAssignment(closeMainModal = true) {
      this.savingEvaluation = true;
      this.errorMessage = "";

      const evaluator1 = this.modalEvaluator1List.filter(f => f.selected);
      const evaluator2 = this.modalEvaluationType === "double"
        ? this.modalEvaluator2List.filter(f => f.selected)
        : [];

      const mainAPI = this.$store.getters.getAPIKey.mainAPI;

      try {
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/evaluations`,
          {
            code: this.modalEvaluationGroupCode,
            evaluator_1_ids: evaluator1.map(f => f.id),
            evaluator_2_ids: evaluator2.map(f => f.id),
          }
        );

        this.$set(this.evaluationsByGroup, this.modalEvaluationGroupCode, {
          evaluation_type: this.modalEvaluationType,
          evaluator_1: evaluator1.map(f => ({
            id: f.id,
            name: f.name
          })),
          evaluator_2: evaluator2.map(f => ({
            id: f.id,
            name: f.name
          })),
        });

        this.$toast.success("Evaluation assignment updated.", {
          duration: 3000
        });

        if (closeMainModal) {
          this.$bvModal.hide("coeEvaluationModal");
        }
      } catch {
        this.errorMessage = "Failed to update evaluation assignment.";
      } finally {
        this.savingEvaluation = false;
      }
    },

    async fetchQpSetStudents() {
      this.loadingQpSetStudents = true;
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        const res = await this.$axios.get(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/qp-set-students`
        );
        const byGroup = {};
        (res.data.data || []).forEach(r => {
          if (!byGroup[r.code]) byGroup[r.code] = {};
          byGroup[r.code][r.institution_user_id] = r.set;
        });
        this.qpSetStudentsByGroup = byGroup;
      } catch {
        this.errorMessage = "Failed to load question paper set student mappings.";
      } finally {
        this.loadingQpSetStudents = false;
      }
    },

    openQpSetStudentsModal(grp) {
      this.modalStudentSetGroupCode = grp.code;
      const savedSets = this.qpSetStudentsByGroup[grp.code] || {};
      this.modalStudentSetList = grp.totalSelectedStudents.map(s => ({
        institution_user_id: s.institution_user_id,
        roll_number: s.roll_number,
        register_no: s.register_no,
        name: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
        set: savedSets[s.institution_user_id] || null,
      }));
      this.errorMessage = "";
      this.$bvModal.show("coeQpSetStudentsModal");
    },

    async saveQpSetStudents() {
      this.savingStudentSets = true;
      this.errorMessage = "";
      const assignments = this.modalStudentSetList
        .filter(s => s.set)
        .map(s => ({ institution_user_id: s.institution_user_id, set: s.set }));
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/qp-set-students`,
          { code: this.modalStudentSetGroupCode, assignments }
        );

        const byStudentId = {};
        assignments.forEach(a => { byStudentId[a.institution_user_id] = a.set; });
        this.$set(this.qpSetStudentsByGroup, this.modalStudentSetGroupCode, byStudentId);
        this.$toast.success("Student set assignments updated.", { duration: 3000 });
        this.$bvModal.hide("coeQpSetStudentsModal");
      } catch {
        this.errorMessage = "Failed to update student set assignments.";
      } finally {
        this.savingStudentSets = false;
      }
    },

    async fetchExamDates() {
      this.loadingExamDates = true;
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        const res = await this.$axios.get(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/exam-dates`
        );
        const byGroup = {};
        (res.data.data || []).forEach(r => {
          byGroup[r.code] = {
            exam_date: r.exam_date || "",
            start_time: (r.start_time || "").slice(0, 5),
          };
        });
        this.examDatesByGroup = byGroup;
      } catch {
        this.errorMessage = "Failed to load exam dates.";
      } finally {
        this.loadingExamDates = false;
      }
    },

    // "14:30" as stored becomes "2:30 PM" for display.
    formatTime12Hour(time) {
      if (!time) return "";
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours, 10);
      const period = hour < 12 ? "AM" : "PM";
      return `${hour % 12 || 12}:${minutes} ${period}`;
    },

    examDateSummary(code) {
      const saved = this.examDatesByGroup[code];
      if (!saved || !saved.exam_date) return "";
      const [year, month, day] = saved.exam_date.split("-");
      return saved.start_time
        ? `${day}-${month}-${year} ${this.formatTime12Hour(saved.start_time)}`
        : `${day}-${month}-${year}`;
    },

    // One popup for the whole group, so the exam timetable is set in a single place.
    openExamDatesModal() {
      this.modalExamDateList = this.displayGroups.map(grp => {
        const saved = this.examDatesByGroup[grp.code] || {};
        return {
          code: grp.code,
          name: grp.courses[0].name,
          exam_date: saved.exam_date || "",
          start_time: saved.start_time || "",
        };
      });
      this.errorMessage = "";
      this.$bvModal.show("coeExamDatesModal");
    },

    async saveExamDates() {
      this.savingExamDates = true;
      this.errorMessage = "";
      const examDates = this.modalExamDateList.map(row => ({
        code: row.code,
        exam_date: row.exam_date || null,
        start_time: row.start_time || null,
      }));
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/exam-dates`,
          { exam_dates: examDates }
        );

        const byGroup = {};
        examDates.forEach(row => {
          byGroup[row.code] = {
            exam_date: row.exam_date || "",
            start_time: row.start_time || "",
          };
        });
        this.examDatesByGroup = byGroup;
        this.$toast.success("Exam dates updated.", { duration: 3000 });
        this.$bvModal.hide("coeExamDatesModal");
      } catch {
        this.errorMessage = "Failed to update exam dates.";
      } finally {
        this.savingExamDates = false;
      }
    },

    async fetchEvaluatorStudents() {
      this.loadingEvaluatorStudents = true;
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        const res = await this.$axios.get(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/evaluator-students`
        );
        const byGroup = {};
        (res.data.data || []).forEach(r => {
          if (!byGroup[r.code]) byGroup[r.code] = { 1: {}, 2: {} };
          byGroup[r.code][r.evaluator_number][r.student_id] = r.institution_user_id;
        });
        this.evaluatorStudentsByGroup = byGroup;
      } catch {
        this.errorMessage = "Failed to load evaluator-student mappings.";
      } finally {
        this.loadingEvaluatorStudents = false;
      }
    },

    openEvaluatorStudentsModal(grp) {
      this.modalEvaluatorStudentsGroupCode = grp.code;
      this.modalEvaluatorStudentsType = grp.evaluation.evaluation_type;
      this.modalRound1Pool = grp.evaluation.evaluator_1;
      this.modalRound2Pool = grp.evaluation.evaluator_2;

      const saved = this.evaluatorStudentsByGroup[grp.code] || { 1: {}, 2: {} };
      const buildList = round => grp.totalSelectedStudents.map(s => ({
        student_id: s.institution_user_id,
        roll_number: s.roll_number,
        register_no: s.register_no,
        name: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
        evaluator_id: (saved[round] || {})[s.institution_user_id] || null,
        checked: false,
      }));
      this.modalRound1List = buildList(1);
      this.modalRound2List = buildList(2);
      this.modalRound1BulkEvaluatorId = null;
      this.modalRound2BulkEvaluatorId = null;
      this.modalRound1RangeInput = "";
      this.modalRound2RangeInput = "";

      this.errorMessage = "";
      this.$bvModal.show("coeEvaluatorStudentsModal");
    },

    evaluatorName(pool, evaluatorId) {
      if (!evaluatorId) return "Unassigned";
      const evaluator = pool.find(e => e.id === evaluatorId);
      return evaluator ? evaluator.name : "Unassigned";
    },

    // Students mapped to each evaluator of the round, plus an "Unassigned" entry (id 0).
    evaluatorStudentCounts(pool, list) {
      const counts = pool.map(e => ({ id: e.id, name: e.name, count: list.filter(s => s.evaluator_id === e.id).length }));
      const assigned = counts.reduce((sum, item) => sum + item.count, 0);
      counts.push({ id: 0, name: "Unassigned", count: list.length - assigned });
      return counts;
    },

    // No Select button: a typed comma (end of a range/number) or Enter applies the range.
    onRangeKeyup(event, round) {
      if (event.key === "," || event.key === "Enter") this.selectRoundRange(round);
    },

    // Ticks the students whose Sl.No is listed in the range input ("1-10, 15"),
    // replacing whatever was ticked before.
    selectRoundRange(round) {
      const list = round === 1 ? this.modalRound1List : this.modalRound2List;
      const input = round === 1 ? this.modalRound1RangeInput : this.modalRound2RangeInput;

      const wanted = new Set();
      input.split(",").forEach(part => {
        const [from, to] = part.split("-").map(value => parseInt(value.trim(), 10));
        if (isNaN(from)) return;
        const last = isNaN(to) ? from : to;
        for (let serial = Math.min(from, last); serial <= Math.max(from, last); serial++) {
          wanted.add(serial);
        }
      });

      list.forEach((student, index) => { student.checked = wanted.has(index + 1); });
    },

    bulkAssignRound(round) {
      const list = round === 1 ? this.modalRound1List : this.modalRound2List;
      const evaluatorId = round === 1 ? this.modalRound1BulkEvaluatorId : this.modalRound2BulkEvaluatorId;
      list.forEach(s => {
        if (s.checked) {
          s.evaluator_id = evaluatorId;
          s.checked = false;
        }
      });
      this.clearRoundRangeInput(round);
    },

    bulkUnassignRound(round) {
      const list = round === 1 ? this.modalRound1List : this.modalRound2List;
      list.forEach(s => {
        if (s.checked) {
          s.evaluator_id = null;
          s.checked = false;
        }
      });
      this.clearRoundRangeInput(round);
    },

    clearRoundRangeInput(round) {
      if (round === 1) this.modalRound1RangeInput = "";
      else this.modalRound2RangeInput = "";
    },

    async saveEvaluatorStudents() {
      this.savingEvaluatorStudents = true;
      this.errorMessage = "";
      const round1 = this.modalRound1List
        .filter(s => s.evaluator_id)
        .map(s => ({ student_id: s.student_id, institution_user_id: s.evaluator_id }));
      const round2 = this.modalEvaluatorStudentsType === "double"
        ? this.modalRound2List.filter(s => s.evaluator_id).map(s => ({ student_id: s.student_id, institution_user_id: s.evaluator_id }))
        : [];
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/evaluator-students`,
          { code: this.modalEvaluatorStudentsGroupCode, round1, round2 }
        );

        const byStudent1 = {};
        round1.forEach(a => { byStudent1[a.student_id] = a.institution_user_id; });
        const byStudent2 = {};
        round2.forEach(a => { byStudent2[a.student_id] = a.institution_user_id; });
        this.$set(this.evaluatorStudentsByGroup, this.modalEvaluatorStudentsGroupCode, { 1: byStudent1, 2: byStudent2 });
        this.$toast.success("Evaluator-student assignments updated.", { duration: 3000 });
        this.$bvModal.hide("coeEvaluatorStudentsModal");
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.message)
          || "Failed to update evaluator-student assignments.";
      } finally {
        this.savingEvaluatorStudents = false;
      }
    },

    openQuestionLog(grp, set) {
      this.questionLogTitle = `${grp.code} - ${grp.courses[0].name} - Set ${set.label} : Question Paper Log`;
      this.questionLogExamId = set.exam_id;
      this.$bvModal.show("coeQuestionLogModal");
    },

    async viewQuestionPaper(set) {
      this.errorMessage = "";
      try {
        const examination = this.$store.getters.getAPIKey.examination;
        const res = await this.$axios.get(`${examination}/${set.exam_id}`);
        const exam = res.data;
        exam.course_id = exam.course && exam.course.length ? exam.course[0].id : set.course_id;
        exam.usergroup_id = set.usergroup_id;
        exam.config_group_id = exam.config_group_id || set.config_group_id;

        //this.$bvModal.hide("coeModal");
        //this.$root.subContent.subContentComponent = "ManageExamQuestions";
        //this.$root.subContent.subContentData = {
        //  exam,
        //  programme_config_group_id: set.config_group_id,
        //   readOnly: true,
        //};
        //this.$root.subContent.enableMainContent = false;

        this.selectedExam = exam;
        this.selectedProgrammeConfigGroupId =set.config_group_id;
        // Read-only mode
        this.selectedReadOnly = true;
        // Open Question Paper modal FIRST
        this.$bvModal.show(
          "questionPaperModal"
        );
        
      } catch {
        this.errorMessage = "Failed to open question paper.";
      }
    },
    closeQuestionPaperModal() {
      this.$bvModal.hide("questionPaperModal");

      this.selectedExam = null;
      this.selectedProgrammeConfigGroupId = null;
      this.selectedReadOnly = false;
    },

    openQpFacultyModal(grp, set) {
      this.modalQpGroupCode = grp.code;
      this.modalQpSetLabel = set.label;
      this.modalQpTitle = `${grp.courses[0].name} - Set ${set.label} : Assign Faculties`;
      this.modalQpFacultyList = this.faculties.map(f => ({
        ...f,
        selected: set.faculty_ids.includes(f.id),
      }));
      this.errorMessage = "";
      this.$bvModal.show("coeQpFacultyModal");
    },

    async removeFaculty(faculty) {
     faculty.selected = false;
     this.saveQpFaculties();
    },

    async saveQpEditWindow(grp, set, changes) {
      const current = (this.qpStatusByGroup[grp.code] || {})[set.label] || {};
      const next = {
        edit_until: set.edit_until,
        allow_edit_after_deadline: set.allow_edit_after_deadline,
        ...changes,
      };
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/question-paper-sets/edit-window`,
          { code: grp.code, set: set.label, ...next }
        );
        const groupStatus = { ...(this.qpStatusByGroup[grp.code] || {}) };
        groupStatus[set.label] = { ...current, ...next };
        this.$set(this.qpStatusByGroup, grp.code, groupStatus);
        this.$toast.success(`Edit date updated for Set ${set.label}.`, { duration: 3000 });
      } catch {
        this.errorMessage = "Failed to update question paper edit date.";
      }
    },

    async saveQpFaculties(closeMainModal = true) {
      this.savingQpFaculties = true;
      this.errorMessage = "";
      const selected = this.modalQpFacultyList.filter(f => f.selected);
      const facultyIds = selected.map(f => f.id);
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/course-groups/${this.groupId}/question-paper-sets`,
          { code: this.modalQpGroupCode, set: this.modalQpSetLabel, faculty_ids: facultyIds }
        );
        const groupSets = { ...(this.qpSetsByGroup[this.modalQpGroupCode] || {}) };
        groupSets[this.modalQpSetLabel] = {
          faculty_ids: facultyIds,
          faculty_names: selected.map(f => f.name),
        };
        this.$set(this.qpSetsByGroup, this.modalQpGroupCode, groupSets);
        this.$toast.success(`Faculties updated for Set ${this.modalQpSetLabel}.`, { duration: 3000 });
        if (closeMainModal){
          this.$bvModal.hide("coeQpFacultyModal");
        }
      } catch {
        this.errorMessage = "Failed to assign faculties to question paper set.";
      } finally {
        this.savingQpFaculties = false;
      }
    },

    openAddStudentsModal(course) {
      this.modalTitle = course.name + " - " + (course.class_code || "Offering") + " : Add Exam Students";
      this.modalCourseId = course.id;
      this.modalCourseStudents = course.enrolled.map(s => ({
        ...s,
        selected: course.selectedIds.includes(s.institution_user_id),
      }));
      this.errorMessage = "";
      this.$bvModal.show("coeAddStudentsModal");
    },

    async saveSelectedStudents() {
      this.savingSelection = true;
      this.errorMessage = "";
      const studentIds = this.modalCourseStudents
        .filter(s => s.selected)
        .map(s => s.institution_user_id);
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      try {
        await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.examId}/courses/${this.modalCourseId}/students`,
          { student_ids: studentIds }
        );
        this.$set(this.selectedByCourse, this.modalCourseId, studentIds);
        this.$toast.success("Exam students updated.", { duration: 3000 });
        this.$bvModal.hide("coeAddStudentsModal");
      } catch {
        this.errorMessage = "Failed to update exam students.";
      } finally {
        this.savingSelection = false;
      }
    },
  },
};
</script>



<style>
.group-shaded { background-color: #f8f9fa; }

/* The app-wide rule sticks only the last header row. This table has a two-row header,
   so the first row sticks at the top and the second one right below it (its offset is
   measured in stickyHeaderOffset, since the row height depends on the font size). */
.coe-group-table thead tr {
  position: sticky;
  z-index: 2;
}
.coe-group-table thead tr:first-child {
  top: 0;
}

/* Question Paper header cells and the nested per-set table share these widths;
   the nested table is fixed-layout so its columns follow them, not their content */
.qp-set-col-set { width: 70px; min-width: 70px; }
.qp-set-col-faculty { width: 180px; min-width: 180px; }
.qp-set-col-status { width: 150px; min-width: 150px; }
.qp-set-col-edit-until { width: 190px; min-width: 190px; }

.qp-set-inner-table {
  table-layout: fixed;
  width: 100%;
}
.qp-set-inner-table td {
  overflow-wrap: anywhere;
}

.qp-set-inner-table tr td:first-child {
  border-left: none;
}
.qp-set-inner-table tr td:last-child {
  border-right: none;
}
.qp-set-inner-table tr:first-child td {
  border-top: none;
}
.qp-set-inner-table tr:last-child td {
  border-bottom: none;
}

.course-badge {
  border: 1px solid #dee2e6;
  color: #343a40;
  font-weight: 500;
  font-size: 0.8rem;
}

/* Navigation between steps must go through the Back/Next/Finish buttons, not the tab headers. */
.coe-eligibility-tabs .nav-tabs .nav-link {
  pointer-events: none;
  cursor: default;
}
</style>
